"use client";

import { useEffect, useMemo } from "react";
import { Button } from "@dionatanbig2be/big2be-ui";
import { removeNonNumbers } from "@dionatanbig2be/big2be-ui/utils";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { registerCustomer, validateCpfCnpj } from "@/modules/profile";
import { getApiErrorMessage } from "@/shared/helpers/errors";
import { toast } from "@/shared/components";
import { useAuthFormFields } from "@/shell/hooks/useBootstrap";
import { AuthFacilitators } from "./AuthFacilitators";
import { AuthField } from "./AuthField";
import { useAuth } from "../hooks/useAuth";
import { useAuthModal } from "../stores/useAuthModal";
import {
    birthDateToIso,
    buildSchema,
    loginIdentifiers,
    REGISTER_FIELDS,
    visibleFields,
} from "../lib/auth-fields";
import type { Resolver, UseFormSetError } from "react-hook-form";
import type { RegisterFormValues, ResolvedIdentifier } from "../lib/auth-fields";
import type { FormFieldsConfig, LoginField } from "../types/auth-form-fields.dto";
import type { CustomerRegistrationDto, Gender, MaritalStatus } from "@/modules/profile";

/**
 * Identificador para a entrada automática após o cadastro.
 *
 * A loja configura SEPARADAMENTE o que coleta no cadastro e o que aceita no
 * login: é comum pedir CPF (nota fiscal) e autenticar por e-mail. O back
 * recusa qualquer identificador não habilitado — inclusive quando ele vem
 * junto de um válido —, então aqui vai UM só, o primeiro que a loja habilita
 * e o formulário preencheu. `null` quando nenhum serve: aí não há login
 * automático a tentar.
 */
function loginIdentifierFor(
    loginConfig: FormFieldsConfig<LoginField>,
    values: { cpfCnpj: string; email: string; phone: string }
): ResolvedIdentifier | null {
    const byName: Record<string, ResolvedIdentifier | null> = {
        cpfCnpj: values.cpfCnpj ? { cpfCnpj: values.cpfCnpj } : null,
        email: values.email ? { email: values.email } : null,
        phone: values.phone ? { phone: values.phone } : null,
    };

    for (const def of loginIdentifiers(loginConfig)) {
        const identifier = byName[def.name];
        if (identifier) return identifier;
    }

    return null;
}

/**
 * Mapeia o erro do cadastro para o campo correspondente (aparece sob o
 * input) quando dá pra identificar; senão, cai no toast. As mensagens de
 * conflito vêm da nest — casamos pelo conteúdo por falta de código de campo.
 */
function reportRegisterError(
    error: unknown,
    setError: UseFormSetError<RegisterFormValues>,
    visibleNames: Set<string>
): void {
    const message = getApiErrorMessage(
        error,
        "Não foi possível criar a conta. Confira os dados e tente novamente."
    );
    const normalized = message.toLowerCase();

    const field: keyof RegisterFormValues | null = normalized.includes("mail")
        ? "email"
        : normalized.includes("cpf") || normalized.includes("cnpj")
          ? "cpfCnpj"
          : null;

    if (field && visibleNames.has(field)) {
        setError(field, { type: "server", message });
        return;
    }

    toast.error(message);
}

export function RegisterForm({ onSuccess }: { onSuccess?: () => void }) {
    const formFields = useAuthFormFields();
    const config = formFields.register;
    const { login } = useAuth();

    // Campos e validação vêm da config do cliente (register_form_fields)
    const fields = useMemo(() => visibleFields(REGISTER_FIELDS, config), [config]);
    const schema = useMemo(() => buildSchema(REGISTER_FIELDS, config), [config]);

    const {
        control,
        handleSubmit,
        setError,
        setValue,
        formState: { errors, isSubmitting },
    } = useForm<RegisterFormValues>({
        resolver: yupResolver(schema) as unknown as Resolver<RegisterFormValues>,
        // Checkboxes começam como `false` (não `undefined`) — controlados desde
        // o início e sem depender do gotcha de valor vazio na validação.
        defaultValues: { acceptTerms: false, communicationAccept: false },
    });

    const visibleNames = useMemo(() => new Set(fields.map((def) => def.name)), [fields]);

    // Cadastro aberto já preenchido: login social (nome/e-mail do provedor) ou
    // pré-cadastro importado pela loja (só o identificador usado no login).
    const prefill = useAuthModal((state) => state.registerPrefill);
    useEffect(() => {
        if (!prefill) return;
        if (prefill.firstName) setValue("firstName", prefill.firstName);
        if (prefill.lastName) setValue("lastName", prefill.lastName);
        if (prefill.email) setValue("email", prefill.email);
        if (prefill.cpfCnpj) setValue("cpfCnpj", prefill.cpfCnpj);
    }, [prefill, setValue]);

    // Num pré-cadastro trava SÓ o identificador com que a pessoa entrou — editar
    // esse aqui só produziria erro no envio. O outro fica em branco e editável de
    // propósito: é preenchendo ele, e batendo com o que a loja cadastrou, que ela
    // mostra ser dona da conta.
    const isPreRegistration = Boolean(prefill?.isPreRegistration);
    const lockedFields = useMemo(() => {
        if (!isPreRegistration) return new Set<string>();
        return new Set(
            (["cpfCnpj", "email"] as const).filter((field) => prefill?.[field])
        );
    }, [isPreRegistration, prefill]);

    async function onSubmit(values: RegisterFormValues) {
        const cpfCnpj = removeNonNumbers(values.cpfCnpj ?? "");
        const password = values.password ?? "";
        let registered = false;

        // Confirma o documento no back antes de cadastrar; erro de rede não
        // bloqueia (o próprio /register valida de novo).
        if (visibleNames.has("cpfCnpj")) {
            try {
                if (!(await validateCpfCnpj(cpfCnpj))) {
                    setError("cpfCnpj", { type: "server", message: "CPF/CNPJ inválido" });
                    return;
                }
            } catch {
                // segue o fluxo — a validação definitiva é a do cadastro
            }
        }

        try {
            // Dados pessoais só entram no payload quando preenchidos: o back os
            // valida com @IsOptional + @IsIn/@IsDateString, e @IsOptional ignora
            // `undefined` mas NÃO string vazia — mandar "" seria reprovado.
            const dadosPessoais: Partial<CustomerRegistrationDto> = {};
            const birthday = birthDateToIso(values.birthDate);
            if (birthday) dadosPessoais.birthday = birthday;
            if (values.gender) dadosPessoais.gender = values.gender as Gender;
            if (values.profession) dadosPessoais.jobTitle = values.profession;
            if (values.maritalStatus) {
                dadosPessoais.maritalStatus = values.maritalStatus as MaritalStatus;
            }
            if (values.hasChildren) dadosPessoais.hasChildren = values.hasChildren === "sim";

            await registerCustomer({
                cpfCnpj,
                email: values.email ?? "",
                firstName: values.firstName ?? "",
                lastName: values.lastName ?? "",
                phoneNumber: removeNonNumbers(values.phoneNumber ?? ""),
                password,
                communicationAccept: values.communicationAccept,
                // Canal de origem do cadastro (a nest só aceita app/wifi/ecommerce).
                origin: "ecommerce",
                ...dadosPessoais,
            });

            registered = true;

            // Conta criada — autentica direto para não pedir login em seguida.
            const identifier = loginIdentifierFor(formFields.login, {
                cpfCnpj,
                email: values.email ?? "",
                phone: removeNonNumbers(values.phoneNumber ?? ""),
            });
            if (!identifier) throw new Error("Nenhum identificador aceito no login");

            await login({ ...identifier, password });
            toast.success("Conta criada com sucesso.");
            onSuccess?.();
        } catch (error) {
            // Depois da conta criada, só a entrada automática pode ter falhado —
            // e falha de login não é falha de cadastro: tratá-la como erro do
            // formulário faria a pessoa achar que precisa se cadastrar de novo.
            if (registered) {
                // O motivo vai junto: sem ele, "não logou depois do cadastro" era
                // indiagnosticável — o erro real morria aqui.
                console.warn("[register] entrada automática falhou", error);
                toast.success(
                    `Conta criada! Faça login para continuar. (${getApiErrorMessage(error, "falha ao entrar automaticamente")})`
                );
                onSuccess?.();
                return;
            }

            reportRegisterError(error, setError, visibleNames);
        }
    }

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-2 gap-3">
            <AuthFacilitators
                facilitators={config.facilitators}
                actionLabel="register"
                disabled={isSubmitting}
                onSuccess={() => {
                    toast("Login realizado com sucesso.");
                    onSuccess?.();
                }}
                onError={(message) => toast.error(message)}
            />

            {isPreRegistration && (
                <p className="col-span-2 rounded-box bg-base-200 px-3 py-2 text-sm opacity-80">
                    A loja já iniciou seu cadastro. Complete seus dados e defina uma senha para concluir.
                </p>
            )}

            {fields.map((def) => (
                <AuthField
                    key={def.name}
                    def={def}
                    control={control}
                    errors={errors}
                    disabled={isSubmitting || lockedFields.has(def.name)}
                />
            ))}

            <Button
                type="submit"
                tone="primary"
                className="col-span-2"
                isLoading={isSubmitting}
                disabled={isSubmitting}
            >
                Criar conta
            </Button>
        </form>
    );
}
